/**
 * The SHOVRON mark.
 *
 * Inline, and coloured from `--logo-ink` rather than from the file — because the
 * mark must change colour with the theme and an <img> cannot. This shipped for
 * months as `next/image` pointing at `/shovron-logo.svg`, which is the brand
 * package's CREAM variant with `fill="#ece4d3"` baked into the root element. On
 * the dark theme that is correct and it is the primary mark. On the light theme
 * it painted warm off-white onto near-white: drawn, at full opacity, invisible.
 *
 * No CSS could have fixed that where it was. A browser will not let a stylesheet
 * reach inside an <img> to repaint it, so the colour had to stop living in the
 * file. The brand package ships the other half of the answer and says so
 * outright — "shovron-logo-navy.* — Navy mark for LIGHT backgrounds" — and
 * `--logo-ink` in globals.css is where that rule now lives.
 *
 * The geometry below is `client/public/shovron-logo.svg` verbatim, generated
 * from it rather than retyped. That file stays as the repo's only versioned copy
 * of the artwork (the brand package itself is not in git) and is now the source
 * to regenerate this from, not something the app fetches. The one edit is the
 * fill: the root element takes `var(--logo-ink)` and the 55 circles carry no
 * fill of their own, exactly as in the file, so a single token colours the mark.
 *
 * `width`/`height` are intrinsic defaults standing in for what next/image
 * required; every call site passes a Tailwind `size-*`, which wins in the
 * cascade. Inlining also drops a network request and the `priority` preload.
 */
export function Logo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={48}
      height={48}
      fill="var(--logo-ink)"
      className={className}
      role="img"
      aria-label="SHOVRON"
    >
      <circle cx="50.00" cy="50.00" r="5.20"/>
      <circle cx="43.50" cy="43.50" r="3.40"/>
      <circle cx="56.50" cy="43.50" r="3.40"/>
      <circle cx="43.50" cy="56.50" r="3.40"/>
      <circle cx="56.50" cy="56.50" r="3.40"/>
      <circle cx="41.51" cy="41.51" r="4.00"/>
      <circle cx="37.48" cy="37.78" r="3.30"/>
      <circle cx="33.86" cy="33.61" r="2.70"/>
      <circle cx="30.23" cy="30.17" r="2.20"/>
      <circle cx="27.76" cy="30.21" r="1.20" opacity="0.88"/>
      <circle cx="26.63" cy="27.41" r="1.80"/>
      <circle cx="24.22" cy="25.12" r="1.20" opacity="0.88"/>
      <circle cx="23.77" cy="23.90" r="1.50"/>
      <circle cx="58.49" cy="41.51" r="4.00"/>
      <circle cx="62.65" cy="37.90" r="3.30"/>
      <circle cx="66.03" cy="33.50" r="2.70"/>
      <circle cx="68.54" cy="33.93" r="1.22" opacity="0.88"/>
      <circle cx="69.71" cy="30.11" r="2.20"/>
      <circle cx="67.17" cy="32.12" r="1.20" opacity="0.88"/>
      <circle cx="72.80" cy="26.84" r="1.80"/>
      <circle cx="75.84" cy="23.51" r="1.50"/>
      <circle cx="41.51" cy="58.49" r="4.00"/>
      <circle cx="37.79" cy="62.54" r="3.30"/>
      <circle cx="36.00" cy="62.99" r="1.48" opacity="0.88"/>
      <circle cx="33.62" cy="66.15" r="2.70"/>
      <circle cx="33.89" cy="63.70" r="1.22" opacity="0.88"/>
      <circle cx="30.57" cy="70.17" r="2.20"/>
      <circle cx="26.87" cy="72.83" r="1.80"/>
      <circle cx="25.83" cy="73.31" r="1.20" opacity="0.88"/>
      <circle cx="23.88" cy="76.20" r="1.50"/>
      <circle cx="58.49" cy="58.49" r="4.00"/>
      <circle cx="62.54" cy="62.20" r="3.30"/>
      <circle cx="63.66" cy="60.77" r="1.48" opacity="0.88"/>
      <circle cx="66.20" cy="66.33" r="2.70"/>
      <circle cx="68.30" cy="67.61" r="1.22" opacity="0.88"/>
      <circle cx="69.98" cy="69.62" r="2.20"/>
      <circle cx="67.84" cy="69.16" r="1.20" opacity="0.88"/>
      <circle cx="72.76" cy="73.20" r="1.80"/>
      <circle cx="76.17" cy="76.15" r="1.50"/>
      <circle cx="23.86" cy="21.96" r="1.16" opacity="0.55"/>
      <circle cx="22.01" cy="17.76" r="0.89" opacity="0.55"/>
      <circle cx="19.73" cy="18.66" r="1.05" opacity="0.55"/>
      <circle cx="22.47" cy="18.63" r="1.27" opacity="0.55"/>
      <circle cx="80.48" cy="21.38" r="0.74" opacity="0.55"/>
      <circle cx="81.67" cy="19.99" r="1.30" opacity="0.55"/>
      <circle cx="80.30" cy="17.26" r="0.93" opacity="0.55"/>
      <circle cx="77.95" cy="16.65" r="0.98" opacity="0.55"/>
      <circle cx="24.35" cy="79.99" r="0.74" opacity="0.55"/>
      <circle cx="20.88" cy="83.31" r="0.85" opacity="0.55"/>
      <circle cx="18.82" cy="76.98" r="0.75" opacity="0.55"/>
      <circle cx="20.31" cy="79.13" r="1.23" opacity="0.55"/>
      <circle cx="79.45" cy="83.56" r="0.87" opacity="0.55"/>
      <circle cx="80.02" cy="78.42" r="1.23" opacity="0.55"/>
      <circle cx="84.26" cy="80.31" r="0.81" opacity="0.55"/>
      <circle cx="79.69" cy="76.67" r="0.99" opacity="0.55"/>
    </svg>
  );
}
